import React, { useMemo, useState } from 'react';
import { tableService } from '../../services/tableService';
import '../Modal.css';
import './Tables.css';

const defaultColumn = () => ({
  name: '',
  type: 'text',
  nullable: true,
  primary_key: false,
  unique: false,
  default: '',
});

const TableCreateForm = ({ isOpen, onClose, onTableCreated }) => {
  const [tableName, setTableName] = useState('');
  const [description, setDescription] = useState('');
  const [columns, setColumns] = useState([defaultColumn()]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const supportedTypes = useMemo(
    () => [
      'uuid',
      'text',
      'varchar(255)',
      'integer',
      'bigint',
      'boolean',
      'timestamp',
      'date',
      'jsonb',
    ],
    []
  );

  const handleColumnChange = (index, field, value) => {
    setColumns((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      if (field === 'primary_key' && value) {
        return updated.map((col, idx) => ({
          ...col,
          primary_key: idx === index,
        }));
      }

      return updated;
    });

    if (errors[`column-${index}-${field}`]) {
      setErrors((prev) => ({ ...prev, [`column-${index}-${field}`]: '' }));
    }
  };

  const addColumn = () => setColumns((prev) => [...prev, defaultColumn()]);
  const removeColumn = (index) => {
    setColumns((prev) => prev.filter((_, idx) => idx !== index));
  };

  const validate = () => {
    const newErrors = {};
    if (!tableName.trim()) {
      newErrors.tableName = 'Table name is required';
    }

    columns.forEach((col, idx) => {
      if (!col.name.trim()) {
        newErrors[`column-${idx}-name`] = 'Column name is required';
      }
      if (!col.type.trim()) {
        newErrors[`column-${idx}-type`] = 'Type is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setLoading(true);
    const payload = {
      table_name: tableName.trim(),
      description: description.trim(),
      columns: columns.map((col) => ({
        name: col.name.trim(),
        type: col.type.trim(),
        nullable: col.nullable,
        primary_key: col.primary_key,
        unique: col.unique,
        default: col.default === '' ? null : col.default,
      })),
    };

    const result = await tableService.createTable(payload);
    if (result.success) {
      onTableCreated();
    } else {
      setErrors({ general: result.error });
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal tables-modal" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <h3>Create New Table</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <form className="table-form" onSubmit={handleSubmit}>
            {errors.general && (
              <div className="notification error">{errors.general}</div>
            )}

            <div className="form-group">
              <label htmlFor="table-name">Table Name *</label>
              <input
                id="table-name"
                type="text"
                value={tableName}
                onChange={(event) => setTableName(event.target.value)}
                placeholder="example: customer_orders"
                required
              />
              {errors.tableName && <span className="error-text">{errors.tableName}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="table-description">Description</label>
              <textarea
                id="table-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Optional notes about the table"
                rows={3}
              />
            </div>

            <div className="columns-header">
              <h4>Columns</h4>
              <button type="button" className="btn btn-secondary" onClick={addColumn}>
                Add Column
              </button>
            </div>

            <div className="columns-grid">
              {columns.map((column, index) => (
                <div key={`column-${index}`} className="column-card">
                  <div className="column-card-header">
                    <h5>Column {index + 1}</h5>
                    {columns.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-danger btn-small"
                        onClick={() => removeColumn(index)}
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      value={column.name}
                      onChange={(event) => handleColumnChange(index, 'name', event.target.value)}
                      placeholder="example: id"
                      required
                    />
                    {errors[`column-${index}-name`] && (
                      <span className="error-text">{errors[`column-${index}-name`]}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Type *</label>
                    <input
                      list={`type-options-${index}`}
                      type="text"
                      value={column.type}
                      onChange={(event) => handleColumnChange(index, 'type', event.target.value)}
                      placeholder="example: uuid"
                      required
                    />
                    <datalist id={`type-options-${index}`}>
                      {supportedTypes.map((type) => (
                        <option key={type} value={type} />
                      ))}
                    </datalist>
                    {errors[`column-${index}-type`] && (
                      <span className="error-text">{errors[`column-${index}-type`]}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Default</label>
                    <input
                      type="text"
                      value={column.default}
                      onChange={(event) => handleColumnChange(index, 'default', event.target.value)}
                      placeholder="example: uuid_generate_v4()"
                    />
                  </div>

                  <div className="checkbox-row">
                    <label>
                      <input
                        type="checkbox"
                        checked={column.primary_key}
                        onChange={(event) => handleColumnChange(index, 'primary_key', event.target.checked)}
                      />
                      Primary Key
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={!column.nullable}
                        onChange={(event) => handleColumnChange(index, 'nullable', !event.target.checked)}
                      />
                      Required
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={column.unique}
                        onChange={(event) => handleColumnChange(index, 'unique', event.target.checked)}
                      />
                      Unique
                    </label>
                  </div>
                </div>
              ))}
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Creating...' : 'Create Table'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TableCreateForm;
